import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';
import unifiedApi from '../utils/unifiedApi';

const OutstandingBalanceContext = createContext({ balance: null, userTenantId: null, refresh: () => {} });

/** Loads the signed-in user's overdue balance once per app load for the banner, nav badge, and home card. */
export const OutstandingBalanceProvider = ({ children }) => {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [balance, setBalance] = useState(null);
  const [userTenantId, setUserTenantId] = useState(null);

  const refresh = useCallback(async () => {
    if (!isAuthenticated || authLoading) {
      setBalance(null);
      setUserTenantId(null);
      return;
    }
    try {
      const [balanceData, me] = await Promise.all([unifiedApi.getMyBalance(), unifiedApi.getMe()]);
      setBalance(balanceData || null);
      setUserTenantId(me?.userTenantId || null);
    } catch {
      // Non-critical; hide balance UI on failure.
      setBalance(null);
    }
  }, [isAuthenticated, authLoading]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const value = useMemo(() => ({ balance, userTenantId, refresh }), [balance, userTenantId, refresh]);

  return (
    <OutstandingBalanceContext.Provider value={value}>
      {children}
    </OutstandingBalanceContext.Provider>
  );
};

export const useOutstandingBalance = () => useContext(OutstandingBalanceContext);

export const useHasOutstandingBalance = () => {
  const { balance } = useOutstandingBalance();
  return (balance?.totalOverdueCents || 0) > 0;
};
